import { useEffect, useRef } from 'react';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { authService } from '@/lib/auth';
import { useAuthContext } from '@/lib/authContext';
import { useIonRouter } from '@/app/IonicContextProvider';

// Configuration constants
const ANDROID_BACKGROUND_LOGOUT_DELAY_MS = 2 * 60 * 1000; // 2 minutes before logout when backgrounded
const PWA_INACTIVITY_TIMEOUT_MS = 200 * 60 * 1000; // ~200 minutes of inactivity before logout

/**
 * Hook to handle auto-logout based on platform:
 * - Android: Logs out after delay when app goes to background
 * - PWA/Web: Logs out after period of inactivity
 * Preserves phone number for PIN-only login on next open
 */
export function useAutoLogout() {
  const { isAuthenticated, logout } = useAuthContext();
  const router = useIonRouter();
  const appStateListenerRef = useRef<any>(null);
  const backgroundLogoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Track user activity for PWA inactivity timeout
  const resetInactivityTimer = () => {
    lastActivityRef.current = Date.now();
    
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    inactivityTimerRef.current = setTimeout(async () => {
      if (isAuthenticated) {
        await logout();
        router.push('/login', 'root', 'replace');
      }
    }, PWA_INACTIVITY_TIMEOUT_MS);
  };

  // Track user activity events
  useEffect(() => {
    if (!isAuthenticated || Capacitor.isNativePlatform()) {
      return;
    }

    // Check if user should be logged out when tab becomes visible again
    const checkInactivityOnVisibility = async () => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      
      if (timeSinceLastActivity >= PWA_INACTIVITY_TIMEOUT_MS) {
        // User has been inactive longer than timeout, log out immediately
        await logout();
        router.push('/login', 'root', 'replace');
      } else if (await authService.isStoredSessionPastJwtEnd()) {
        await logout();
        router.push('/login', 'root', 'replace');
      } else {
        // User returned within timeout, reset the timer
        resetInactivityTimer();
      }
    };

    // Reset timer on user activity
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      document.addEventListener(event, resetInactivityTimer, true);
    });

    // Check inactivity when tab becomes visible again
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        checkInactivityOnVisibility();
      } else if (document.hidden) {
        // Tab is hidden, clear the inactivity timer (will be checked when tab becomes visible)
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
          inactivityTimerRef.current = null;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start the inactivity timer
    resetInactivityTimer();

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, resetInactivityTimer, true);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [isAuthenticated, router, logout]);

  // Proactive logout shortly before JWT exp so API calls (e.g. patrol scan) are not sent with an expired token
  useEffect(() => {
    if (!isAuthenticated) return;

    return authService.scheduleLogoutAtJwtSessionEnd(async () => {
      await logout();
      router.push('/login', 'root', 'replace');
    });
  }, [isAuthenticated, logout, router]);

  useEffect(() => {
    // Only set up auto-logout if user is authenticated
    if (!isAuthenticated) {
      // Clear any pending timers
      if (backgroundLogoutTimerRef.current) {
        clearTimeout(backgroundLogoutTimerRef.current);
        backgroundLogoutTimerRef.current = null;
      }
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return;
    }

    const handleAppStateChange = async ({ isActive }: { isActive: boolean }) => {
      
      if (!isActive && isAuthenticated) {
        // App went to background - start logout timer
        console.log(`Android: App backgrounded, will logout in ${ANDROID_BACKGROUND_LOGOUT_DELAY_MS / 1000} seconds...`);
        
        backgroundLogoutTimerRef.current = setTimeout(async () => {
          await logout();
          router.push('/login', 'root', 'replace');
        }, ANDROID_BACKGROUND_LOGOUT_DELAY_MS);
      } else if (isActive) {
        // App came to foreground - cancel logout timer
        if (backgroundLogoutTimerRef.current) {
          clearTimeout(backgroundLogoutTimerRef.current);
          backgroundLogoutTimerRef.current = null;
        }
        // If JWT passed client session end while backgrounded, log out immediately
        if (isAuthenticated) {
          void authService.isStoredSessionPastJwtEnd().then(async (past) => {
            if (past) {
              await logout();
              router.push('/login', 'root', 'replace');
            }
          });
        }
      }
    };

    // Set up listener for native platforms only
    if (Capacitor.isNativePlatform()) {
      appStateListenerRef.current = App.addListener('appStateChange', handleAppStateChange);
      
      // Cleanup
      return () => {
        if (appStateListenerRef.current) {
          appStateListenerRef.current.remove();
          appStateListenerRef.current = null;
        }
        if (backgroundLogoutTimerRef.current) {
          clearTimeout(backgroundLogoutTimerRef.current);
          backgroundLogoutTimerRef.current = null;
        }
      };
    }
    
    // For web/PWA, inactivity timeout is handled by the activity tracking effect above
    return undefined;
  }, [isAuthenticated, router, logout]);
}

